import {
    Box,
    Button,
    Grid,
    Hidden,
    Skeleton,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import BreadCrum from "../../../lib/BreadCrum";
import { useHistory } from "react-router";
import useQuery from "../../../utils/useQuery";
import { useEffect, useState } from "react";
import { RootStateType } from "../../../redux-store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { navigationData as NavigationData } from "../types";
import AlternatesOverview from "./alternatesOverview";
import AlternatesSchemeDetails from "./alternatesSchemeDetails";
import AlternatesTransactionDetails from "./alternatesTransactionDetails";
import { ChartIcon, CurrencyIcon, MoneyGrow, PercentageIcons, TotalInvestors } from "../../../assets/SvgIcons";
import { formatCurrency, isModuleAllowed } from "../../../utils/utilityFunctions";
import { ResponsiveCard } from "../mutualFund";
import { totalAlternateCardValuesApi } from "../../../redux-store/actions/alternates";
import { USER_ROLES } from "../../../utils/constant";
import { TotalCardValuesRes } from "../../../redux-store/types/products";
import { showError } from "../../../redux-store/actions/auth";
import en from "../../../lang/en-us"
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";


const tabsList = ['Overview', 'Scheme Details', 'Transaction Details']

type CardProps = {
    Icons: React.ReactNode
    title: string
    value?: string
    color?: string
    percent?: boolean
    percentageValue?: number
    isLoading?: boolean
}
const Card = (props: CardProps) => {
    const { Icons, title, value, color, percent, percentageValue, isLoading } = props
    const { palette, typography } = useTheme()
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', borderRadius: '15px', padding: '0px 18px', height: '102px', background: palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' }} >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {Icons}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', width: '80%', }}>
                {isLoading ? <>
                    <Skeleton animation="wave" sx={{ width: '60%' }} />
                    <Skeleton animation="wave" sx={{ width: '100%' }} />
                </> : <>
                    <Typography variant="h2" color={palette.text.primary} sx={{ lineHeight: '14px', fontWeight: '500' }}>{title}</Typography>
                    {value ? !percent ? <Typography variant="body_500" color={color ? color : palette.text.primary}>{value}</Typography> : <Typography variant="body_500" color={color ? color : palette.text.primary}>{`${value ?? value} (${percentageValue ? percentageValue.toFixed(2) : '0'}%)`}</Typography> :
                        <Typography variant="body_500" color={color ? color : palette.text.primary}>{`${percentageValue ? percentageValue.toFixed(2) : '0'}%`}</Typography>
                    }
                </>}
            </Box>
        </Box>
    )
}

export default function Alternates(): JSX.Element {
    const history = useHistory();

    const { pathname: pathName, state } = history.location
    const navState = state as NavigationData

    const query = useQuery()

    const initialRouter = pathName.split('/').filter((item: string) => item !== '')

    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { label: selectedPortfolioLabel = "", value: selectedPortfolioValue = "" } = selectedPortfolio

    const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId, investorClientId } = useSelector((store: RootStateType) => store.id)
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const { palette, typography } = useTheme()
    const dispatch = useDispatch()

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        ...(!initialRouter.includes('product') ? [{
            name: `${initialRouter[1]} ${selectedPortfolioLabel ? `(${selectedPortfolioLabel})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }] : []),
        {
            name: 'Alternates',
            path: !initialRouter.includes('product') ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` : `/${initialRouter[0]}/${initialRouter[1]}}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }
    ] : [])

    const investorRouterPath = [
        {
            name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
        },
        {
            name: 'Alternates',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }
    ]

    //state
    const [selectedTab, setSelectedTab] = useState(query.get('tab') || 'Overview')
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [cardValues, setCardValues] = useState<TotalCardValuesRes>({} as TotalCardValuesRes)
    const [portfolioNotExistStatus, setPortfolioNotExistStatus] = useState<boolean>(false);

    // console.log('portfolioNotExistStatus', portfolioNotExistStatus)

    const totalCardValuesInvestorLevel = async () => {
        try {
            setIsLoading(true);
            const cardValuesRes = await (dispatch(totalAlternateCardValuesApi({
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: '',
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode : investorClientId,
                portfolioId: selectedPortfolio?.value,
                assetGroup: "AL",
            })) as unknown as Promise<TotalCardValuesRes>)

            if (cardValuesRes?.isSuccess) {
                if (Number(cardValuesRes?.result?.totalInvestedValue ?? 0) === 0) {
                    setPortfolioNotExistStatus(true)
                }
                setCardValues(cardValuesRes)
            } else {
                setPortfolioNotExistStatus(true)
            }

        } catch (e) {
            console.log("Error", e);
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        totalCardValuesInvestorLevel();
    }, [])


    const handleTabChange = (currentTab: string) => {
        setSelectedTab(currentTab)
        history.replace({
            pathname: location.pathname, search: `?tab=${currentTab}`, state: {
                clientCode: navState?.clientCode ?? ""
            }
        })
    }

    return (
        <Box sx={{ marginTop: "15px" }}>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
            <Grid
                container
                sx={{
                    marginTop: "25px",
                    display: "flex",
                }}
                spacing={2}
            >
                {/* Card */}
                <Hidden only={'xs'}>
                    <Grid container spacing={2} mt={'25px'} sx={{ height: '100%', alignItems: 'center' }}>
                        <Grid item width={'100%'} sm={6} lg={3}>
                            <Card
                                Icons={<MoneyGrow />}
                                title="Market Value"
                                percent={false}
                                value={portfolioNotExistStatus ? formatCurrency({ value: 0, locale, currencyCode }) : cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue, locale, currencyCode })}`}
                                isLoading={isLoading}
                            />
                        </Grid>
                        <Grid item width={'100%'} sm={6} lg={3}>
                            <Card
                                Icons={<CurrencyIcon />}
                                title="Invested Value"
                                percent={false}
                                value={portfolioNotExistStatus ? formatCurrency({ value: 0, locale, currencyCode }) : cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue, locale, currencyCode })}`}
                                isLoading={isLoading}
                            />
                        </Grid>
                        <Grid item width={'100%'} sm={6} lg={3}>
                            <Card
                                Icons={<ChartIcon />}
                                title="Gain/Loss"
                                value={portfolioNotExistStatus ? "0" : cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss, locale, currencyCode })}`}
                                color={Number(cardValues?.result?.gainLoss ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                                isLoading={isLoading}
                            />
                        </Grid>
                        {initialRouter.includes('product') ?
                            <Grid item width={'100%'} sm={6} lg={3}>
                                <Card
                                    Icons={<TotalInvestors />}
                                    value={portfolioNotExistStatus ? "0" : `${cardValues?.result?.totalInvestors || 0}`}
                                    title="Total Investors"
                                    percent={false}
                                    isLoading={isLoading}
                                />
                            </Grid>
                            :
                            <Grid item width={'100%'} sm={6} lg={3}>
                                <Card
                                    Icons={<PercentageIcons viewBox="0 0 57 40" />}
                                    // value={portfolioNotExistStatus ? "0" : `${cardValues?.result?.irr.toFixed(2) || 0}`}
                                    title="Absolute Return %"
                                    percent={true}
                                    isLoading={isLoading}
                                    color={Number(cardValues?.result?.percentage ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                                    percentageValue={cardValues?.result?.percentage}
                                />
                            </Grid>
                        }
                    </Grid>
                </Hidden>
                <Hidden smUp={true}>
                    <Box sx={{ width: '100%', borderRadius: '15px', flexDirection: 'column', background: palette.background.paper, padding: '20px 18px', gap: '25px', mt: '30px' }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                            <ResponsiveCard title="Market Value" value={portfolioNotExistStatus ? formatCurrency({ value: 0, locale, currencyCode }) : cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue, locale, currencyCode })}`} />
                            <ResponsiveCard title="Invested Value" value={portfolioNotExistStatus ? formatCurrency({ value: 0, locale, currencyCode }) : cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue, locale, currencyCode })}`} align="right" />
                        </Box>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '25px' }}>
                            <ResponsiveCard title="Gain/Loss" value={portfolioNotExistStatus ? "0" : cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss, locale, currencyCode })}`} color={palette.success[800]} />
                            <ResponsiveCard title="Total Investors" value={portfolioNotExistStatus ? "0" : `${cardValues?.result?.totalInvestors?.toFixed(2) || 0}`} color={palette.success[800]} align="right" />
                        </Box>
                    </Box>
                </Hidden>
            </Grid>

            <Box
                sx={(theme: Theme) => ({
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: '10px',
                    paddingBottom: '15px',
                    borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                    overflowX: 'auto',
                    marginTop: "50px",
                    width: '100%',
                })}
                role="tablist"
            >
                {tabsList.map((item, index) => {
                    const tabId = `alt-tab-${item.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`
                    return (
                        <Box
                            component={'button'}
                            key={item}
                            id={tabId}
                            aria-controls="alt-tabpanel"
                            aria-selected={selectedTab === item}
                            role="tab"
                            disabled={portfolioNotExistStatus}
                            onClick={() => portfolioNotExistStatus ? null : handleTabChange(item)}
                            sx={{
                                padding: '5px 15px',
                                background: selectedTab === item ? palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: portfolioNotExistStatus ? 'not-allowed' : 'pointer',
                                opacity: portfolioNotExistStatus ? 0.5 : 1
                            }}>
                            <Typography variant="body_500" color={selectedTab === item ? palette.textDark.primary : palette.grey[700]} sx={{ fontFamily: typography.fontFamily, }}>{item}</Typography>
                        </Box>
                    )
                })}
            </Box>
            <Box
                id="alt-tabpanel"
                role="tabpanel"
                aria-labelledby={`alt-tab-${selectedTab.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                sx={{ marginTop: "25px" }}
            >
                {portfolioNotExistStatus ?
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment />
                    </Box>
                    :
                    (
                        <>
                            {selectedTab === 'Overview' && <AlternatesOverview clientCode={navState?.clientCode ?? ""} />}
                            {selectedTab === 'Scheme Details' && <AlternatesSchemeDetails clientCode={navState?.clientCode ?? ""} />}
                            {selectedTab === 'Transaction Details' && <AlternatesTransactionDetails clientCode={navState?.clientCode ?? ""} />}
                        </>
                    )}
            </Box>
        </Box >
    )
}